import React, { useContext, useEffect, useRef, useState } from "react";
import { Route } from "react-router-dom";
import { ServiceContext } from "@msx/platform-services";
import { Label, Stack, Spinner, SpinnerSize, PrimaryButton } from "@fluentui/react";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { isHomeTenantUser, hasGroupsOverage, checkMemberGroupsFallback, FallbackResult } from "../../../app/pages/Authorize/AuthorizeUtility";
import { isFeatureEnabled } from "../../../app/services/FeatureFlagService";

const MSFT_TENANT = process.env.REACT_APP_AD_TENANT;

const appInsights = new ApplicationInsights({
  config: { instrumentationKey: process.env.REACT_APP_INSTRUMENTATION_KEY },
});
appInsights.loadAppInsights();

// Configurable messages — change in .env files, no code change needed
const ACCESS_DENIED_TITLE = process.env.REACT_APP_ACCESS_DENIED_TITLE || "You are not authorized to access MCAPSHelp. This app is available to members of the Microsoft organization only.";
const ACCESS_DENIED_STALE_HINT = process.env.REACT_APP_ACCESS_DENIED_STALE_HINT || "Already have access? try";
const ACCESS_DENIED_REFRESH_LABEL = process.env.REACT_APP_ACCESS_DENIED_REFRESH_LABEL || "signing out and signing back in with your @microsoft.com account";

/**
 * React Router v5 route guard using existing ServiceContext + authClient.authContext.
 * No new packages required — uses the same MSAL access pattern as V2_Shell and AuthorizeUtility.
 *
 * Renders AccessDenied with two distinct reasons:
 *  - "wrong-tenant": user signed in with non-Microsoft tenant
 *  - "missing-role": user lacks BasicAccess App Role (stale token or not in group)
 *
 * ICM 797003066 overage fix (v2):
 *  When roles[] is empty and the user is a home-tenant member, attempts
 *  POST /me/checkMemberGroups to verify group membership via Graph API fallback.
 */
const PrivateRoute = ({ component: Component, render: renderProp, role, ...rest }: any) => {
  const { authClient } = useContext(ServiceContext);
  const auth = (authClient as any)?.authContext;
  const account = auth?.getAllAccounts()?.[0];
  const redirectTriggered = useRef(false);

  // Overage fallback state
  const [fallbackStatus, setFallbackStatus] = useState<"idle" | "loading" | "granted" | "denied" | "error">("idle");
  const [retryCount, setRetryCount] = useState(0);
  const fallbackAttempted = useRef(false);

  // Move loginRedirect into useEffect to avoid render-time side effects
  // and prevent MSAL interaction_in_progress race condition
  useEffect(() => {
    if (!account && auth && !redirectTriggered.current) {
      redirectTriggered.current = true;
      appInsights.trackTrace({
        message: `PrivateRoute no-account — triggering loginRedirect`,
      });
      auth.loginRedirect().catch((err: any) => {
        console.error("PrivateRoute loginRedirect error:", err);
        redirectTriggered.current = false;
      });
    }
  }, [account, auth]);

  // Overage fallback effect — triggers when roles are empty and conditions are met
  useEffect(() => {
    if (!account) return;
    if (account.idTokenClaims?.tid !== MSFT_TENANT) return;

    // Security: only trigger fallback for BasicAccess role
    const FALLBACK_ROLE = "BasicAccess";
    if (role !== FALLBACK_ROLE) return;

    const roles: string[] = account.idTokenClaims?.roles ?? [];
    if (roles.includes(role)) return; // Already authorized — no fallback needed

    const claims = account.idTokenClaims;

    const shouldFallback = hasGroupsOverage(claims) ||
     (roles.length === 0 && isHomeTenantUser(claims) && isFeatureEnabled("IsGroupOverageFallbackEnabled"));

    if (!shouldFallback) return;
    if (fallbackAttempted.current && retryCount === 0) return;

    fallbackAttempted.current = true;
    setFallbackStatus("loading");

    checkMemberGroupsFallback(authClient)
      .then((result: FallbackResult) => {
        setFallbackStatus(result);
      })
      .catch(() => {
        setFallbackStatus("error");
      });
  }, [account, role, retryCount]);

  return (
    <Route
      {...rest}
      render={(props) => {
        const oid = account?.idTokenClaims?.oid || "unknown";
        const upn = account?.username || "unknown";

        // No account yet — useEffect will handle loginRedirect
        if (!account) {
          return null;
        }

        // Wrong tenant — distinct message
        if (account.idTokenClaims?.tid !== MSFT_TENANT) {
          appInsights.trackTrace({
            message: `PrivateRoute denied reason=wrong-tenant upn=${upn} oid=${oid} tid=${account.idTokenClaims?.tid}`,
          });
          return (
            <AccessDenied
              reason="wrong-tenant"
              message={`Signed in as ${account.username} — this app requires a @microsoft.com account`}
            />
          );
        }

        // Fast path — role present in token
        const roles: string[] = account.idTokenClaims?.roles ?? [];
        if (roles.includes(role)) {
          appInsights.trackTrace({
            message: `PrivateRoute allowed upn=${upn} oid=${oid} role=${role}`,
          });
          return Component ? <Component {...props} /> : renderProp?.(props);
        }

        // Overage fallback path — check if fallback is applicable
        // Security: restrict fallback to BasicAccess only, since checkMemberGroupsFallback
        // verifies only REACT_APP_USER_AUTH_GROUP_ID (the BasicAccess group).
        const FALLBACK_ROLE = "BasicAccess";
        const claims = account.idTokenClaims;
        const shouldFallback = role === FALLBACK_ROLE && (hasGroupsOverage(claims) ||
          (roles.length === 0 && isHomeTenantUser(claims) && isFeatureEnabled("IsGroupOverageFallbackEnabled")));

        if (shouldFallback) {
          // Loading — Graph call in progress
          if (fallbackStatus === "loading" || fallbackStatus === "idle") {
            return (
              <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: "calc(80vh)" } }}>
                <Spinner size={SpinnerSize.large} label="Verifying your access..." />
              </Stack>
            );
          }

          // Granted via Graph fallback
          if (fallbackStatus === "granted") {
            appInsights.trackTrace({
              message: `PrivateRoute allowed via overage-fallback upn=${upn} oid=${oid} role=${role}`,
            });
            return Component ? <Component {...props} /> : renderProp?.(props);
          }

          // Error — show retry option (SPA resilience)
          if (fallbackStatus === "error") {
            return (
              <Stack horizontalAlign="center" verticalAlign="center" styles={{ root: { minHeight: "calc(80vh)" } }} tokens={{ childrenGap: 12 }}>
                <Label style={{ fontSize: "14pt" }}>We couldn't verify your access. Please try again.</Label>
                <PrimaryButton text="Retry" onClick={() => { setFallbackStatus("idle"); setRetryCount(c => c + 1); }} />
              </Stack>
            );
          }

          // Denied via Graph (user not in group)
          // Fall through to AccessDenied below
        }

        // Missing role — no fallback available or fallback denied
        appInsights.trackTrace({
          message: `PrivateRoute denied reason=missing-role upn=${upn} oid=${oid} roles=[${roles.join(",")}]`,
        });
        return (
          <AccessDenied
            reason="missing-role"
            onRefresh={() => {
              // Clear MSAL cache and redirect to AAD login to get a fresh ID token with updated roles
              auth.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
            }}
          />
        );
      }}
    />
  );
};

/**
 * Two-reason AccessDenied component.
 * Shows contextual message depending on whether the user is in the wrong tenant
 * or simply missing the required App Role assignment.
 */
const AccessDenied = ({
  reason,
  message,
  onRefresh,
}: {
  reason: "missing-role" | "wrong-tenant";
  message?: string;
  onRefresh?: () => void;
}) => (
  <Stack
    horizontalAlign="center"
    verticalAlign="center"
    styles={{ root: { minHeight: "calc(80vh)" } }}
    tokens={{ childrenGap: 12 }}
  >
    <Label style={{ fontSize: "20pt" }}>{ACCESS_DENIED_TITLE}</Label>
    {reason === "missing-role" && (
      <Label style={{ fontSize: "12pt", color: "#605e5c" }}>
        {ACCESS_DENIED_STALE_HINT}{" "}
        <a
          onClick={onRefresh}
          style={{ cursor: "pointer", color: "#0078d4" }}
        >
          {ACCESS_DENIED_REFRESH_LABEL}
        </a>
      </Label>
    )}
    {reason === "wrong-tenant" && <Label>{message}</Label>}
  </Stack>
);

export default PrivateRoute;
